import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { centsToInput, parseMoney } from "../lib/format";
import {
  Checkbox,
  DateField,
  FieldGrid,
  MoneyField,
  NumberField,
  RangeField,
  SegmentedControl,
  SelectField,
  TimeRangeField,
  WeekdayField,
} from "./ui/form";
import type { HistoryProfile, HistorySequence } from "../types/api";

/**
 * The recipe for a player's generated history, as a form.
 *
 * One component, used both when the account is created and when its profile is
 * edited later — the two are the same set of questions, and an operator who
 * learns the shape on the way in should meet it unchanged on the way back.
 *
 * It is laid out as the sentence an operator actually says: *when* they played,
 * what a playing day looks like, *how* they played, then how much money moved.
 * How often money moves is one share: that many playing days are Deposit → bets
 * → Withdraw and the rest are bets only. Beyond that only the amounts are asked.
 */

/** `YYYY-MM-DD` in the operator's own zone — what a date input holds. */
export function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const today = () => isoDay(new Date());

/** The zone this browser is in — what the operator means by "13:00". */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Every zone the browser knows, each with its offset now, for the picker.
 *
 * Computed once: the list is a few hundred long and does not change while the
 * console is open. A browser without `supportedValuesOf` gets a short list of
 * the zones this product is actually run from, plus the operator's own.
 */
let zoneOptions: { value: string; label: string }[] | null = null;

export function timeZoneOptions(current: string): { value: string; label: string }[] {
  if (!zoneOptions) {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    const zones = intl.supportedValuesOf?.("timeZone") ?? [
      "Asia/Kuala_Lumpur",
      "Asia/Singapore",
      "Asia/Shanghai",
      "Asia/Hong_Kong",
      "Asia/Bangkok",
      "Asia/Jakarta",
      "Asia/Manila",
      "UTC",
    ];
    zoneOptions = zones.map((zone) => ({ value: zone, label: `${zone} (${utcOffset(zone)})` }));
  }
  const missing = [current, browserTimeZone()].filter(
    (zone) => zone && !zoneOptions!.some((option) => option.value === zone),
  );
  return [
    ...missing.map((zone) => ({ value: zone, label: `${zone} (${utcOffset(zone)})` })),
    ...zoneOptions,
  ];
}

/** `UTC+08:00` — the zone's offset at this moment. */
function utcOffset(timeZone: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" })
      .formatToParts(new Date())
      .find((entry) => entry.type === "timeZoneName");
    // `longOffset` reads "GMT+08:00", or a bare "GMT" for UTC itself.
    const offset = part?.value.replace("GMT", "") ?? "";
    return `UTC${offset || "+00:00"}`;
  } catch {
    return "";
  }
}

/** `"13:30"` → 810. Null for anything that is not a clock time. */
function clockMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number(match[2]) < 60 && minutes <= 24 * 60 ? minutes : null;
}

/** How long the window is, in minutes — over midnight when the end is not after the start. */
export function windowMinutes(window: { start: string; end: string }): number | null {
  const start = clockMinutes(window.start);
  const end = clockMinutes(window.end);
  if (start === null || end === null || start === end) return null;
  return end > start ? end - start : end + 24 * 60 - start;
}

/**
 * The profile a new account starts on: a year of twice-weekly winning play.
 *
 * Not empty defaults. This dialog exists because somebody asked for exactly this
 * shape of record, and an operator who wants it should be able to press Create
 * without filling anything in — while everything remains theirs to change.
 */
export function defaultProfile(): HistoryProfile {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from = new Date(to);
  from.setFullYear(from.getFullYear() - 1);

  return {
    from: isoDay(from),
    to: isoDay(to),
    pattern: "weekdays",
    daysOfWeek: [1, 4],
    intervalDays: { min: 4, max: 6 },
    betsPerDay: { min: 1, max: 2 },
    timeZone: browserTimeZone(),
    window: { start: "13:00", end: "23:00" },
    sequence: { settle: "each", gapMinutes: { min: 1, max: 8 } },
    stakeCents: { min: 10_000, max: 50_000 },
    winRatePercent: 100,
    cashFlowPercent: 100,
    deposits: {
      amountCents: { min: 100_000, max: 500_000 },
      autoTopUp: true,
    },
    withdrawals: {
      percentOfBalance: { min: 40, max: 70 },
      minCents: 10_000,
    },
  };
}

/** Sunday first, so the index is the API's day number. */
const DAY_NUMBERS = [0, 1, 2, 3, 4, 5, 6] as const;

const WIN_RATES = ["100", "90", "80", "75", "60", "50", "40", "25", "0"] as const;

export function HistoryProfileForm({
  value,
  onChange,
  disabled,
}: {
  value: HistoryProfile;
  onChange: (profile: HistoryProfile) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const dayLabels = DAY_NUMBERS.map((day) => t(`day.short.${day}` as TranslationKey));
  const set = <K extends keyof HistoryProfile>(key: K, next: HistoryProfile[K]) =>
    onChange({ ...value, [key]: next });
  const setSequence = (next: Partial<HistorySequence>) =>
    set("sequence", { ...value.sequence, ...next });

  const windowLength = windowMinutes(value.window);
  const windowCrossesMidnight =
    (clockMinutes(value.window.end) ?? 0) <= (clockMinutes(value.window.start) ?? 0);

  return (
    <div className="space-y-5">
      {/* ── When they played ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <p className="eyebrow">{t("history.section.when")}</p>

        <FieldGrid>
          <DateField
            label={t("history.from")}
            value={value.from}
            onChange={(next) => set("from", next)}
            max={value.to || today()}
            disabled={disabled}
            required
          />
          <DateField
            label={t("history.to")}
            value={value.to}
            onChange={(next) => set("to", next)}
            max={today()}
            disabled={disabled}
            required
          />
        </FieldGrid>

        <SegmentedControl
          value={value.pattern}
          onChange={(next) => set("pattern", next)}
          ariaLabel={t("history.pattern")}
          options={[
            { value: "weekdays", label: t("history.pattern.weekdays") },
            { value: "interval", label: t("history.pattern.interval") },
          ]}
        />

        {value.pattern === "weekdays" ? (
          <WeekdayField
            label={t("history.days")}
            value={value.daysOfWeek}
            onChange={(next) => set("daysOfWeek", next)}
            labels={dayLabels}
            hint={t("history.daysHint")}
            error={value.daysOfWeek.length === 0 ? t("history.daysError") : null}
            disabled={disabled}
            required
          />
        ) : (
          <RangeField
            label={t("history.intervalDays")}
            value={value.intervalDays}
            onChange={(next) => set("intervalDays", next)}
            hint={t("history.intervalDaysHint")}
            suffix={t("history.intervalDays.suffix")}
            min={1}
            max={90}
            error={
              value.intervalDays.max < value.intervalDays.min ? t("history.rangeError") : null
            }
            disabled={disabled}
            required
          />
        )}

        <FieldGrid>
          <RangeField
            label={t("history.betsPerDay")}
            value={value.betsPerDay}
            onChange={(next) => set("betsPerDay", next)}
            hint={t("history.betsPerDayHint")}
            min={1}
            max={20}
            disabled={disabled}
            error={
              value.betsPerDay.max < value.betsPerDay.min ? t("history.rangeError") : null
            }
            required
          />
        </FieldGrid>
      </section>

      {/* ── The day ──────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <p className="eyebrow">{t("history.section.day")}</p>

        <TimeRangeField
          label={t("history.window")}
          value={value.window}
          onChange={(next) => set("window", next)}
          hint={
            windowLength !== null && windowCrossesMidnight
              ? t("history.windowOvernight")
              : t("history.windowHint")
          }
          error={windowLength === null ? t("history.windowError") : null}
          disabled={disabled}
          required
        />
        <SelectField
          label={t("history.timeZone")}
          value={value.timeZone}
          onChange={(next) => set("timeZone", next)}
          options={timeZoneOptions(value.timeZone)}
          hint={t("history.timeZoneHint")}
          disabled={disabled}
        />

        <SegmentedControl
          value={value.sequence.settle}
          onChange={(settle) => setSequence({ settle })}
          ariaLabel={t("history.settle")}
          options={[
            { value: "each", label: t("history.settle.each") },
            { value: "together", label: t("history.settle.together") },
          ]}
        />
        <RangeField
          label={t("history.gap")}
          value={value.sequence.gapMinutes}
          onChange={(gapMinutes) => setSequence({ gapMinutes })}
          suffix={t("history.gap.suffix")}
          min={0}
          max={240}
          hint={t("history.gapHint")}
          error={
            value.sequence.gapMinutes.max < value.sequence.gapMinutes.min
              ? t("history.rangeError")
              : null
          }
          disabled={disabled}
        />

        <NumberField
          label={t("history.cashFlow")}
          value={String(value.cashFlowPercent)}
          onChange={(next) => set("cashFlowPercent", Math.min(100, Number(next || 0)))}
          align="right"
          maxLength={3}
          hint={t("history.cashFlowHint")}
          error={value.cashFlowPercent > 100 ? t("history.cashFlowError") : null}
          disabled={disabled}
          required
        />

        <OrderPreview
          sequence={value.sequence}
          bets={Math.max(1, value.betsPerDay.max)}
          cashFlowPercent={value.cashFlowPercent}
        />
      </section>

      {/* ── How they played ──────────────────────────────────────────────── */}
      <section className="space-y-3">
        <p className="eyebrow">{t("history.section.how")}</p>

        <FieldGrid>
          <MoneyField
            label={t("history.minStake")}
            value={centsToInput(value.stakeCents.min)}
            onChange={(next) =>
              set("stakeCents", { ...value.stakeCents, min: parseMoney(next) ?? 0 })
            }
            disabled={disabled}
            required
          />
          <MoneyField
            label={t("history.maxStake")}
            value={centsToInput(value.stakeCents.max)}
            onChange={(next) =>
              set("stakeCents", { ...value.stakeCents, max: parseMoney(next) ?? 0 })
            }
            error={
              value.stakeCents.max < value.stakeCents.min ? t("error.atLeastMinimum") : null
            }
            disabled={disabled}
            required
          />
        </FieldGrid>

        <SelectField
          label={t("history.winRate")}
          value={String(value.winRatePercent)}
          onChange={(next) => set("winRatePercent", Number(next))}
          options={WIN_RATES.map((rate) => ({ value: rate, label: `${rate}%` }))}
          hint={t("history.winRateHint")}
          disabled={disabled}
        />
      </section>

      {/* ── Money in and out ─────────────────────────────────────────────
          How often, as one share of the playing days, then the amounts. A day
          with cash flow opens with one deposit and closes with one withdrawal. */}
      <section className="space-y-3">
        <p className="eyebrow">{t("history.section.money")}</p>
        <p className="text-dim text-[11px]">{t("history.moneyExplain")}</p>

        <FieldGrid>
          <MoneyField
            label={t("history.depositMin")}
            value={centsToInput(value.deposits.amountCents.min)}
            onChange={(next) =>
              set("deposits", {
                ...value.deposits,
                amountCents: { ...value.deposits.amountCents, min: parseMoney(next) ?? 0 },
              })
            }
            disabled={disabled}
            required
          />
          <MoneyField
            label={t("history.depositMax")}
            value={centsToInput(value.deposits.amountCents.max)}
            onChange={(next) =>
              set("deposits", {
                ...value.deposits,
                amountCents: { ...value.deposits.amountCents, max: parseMoney(next) ?? 0 },
              })
            }
            error={
              value.deposits.amountCents.max < value.deposits.amountCents.min
                ? t("error.atLeastMinimum")
                : null
            }
            disabled={disabled}
            required
          />
        </FieldGrid>

        <Checkbox
          label={t("history.autoTopUp")}
          hint={t("history.autoTopUpHint")}
          checked={value.deposits.autoTopUp}
          onChange={(next) => set("deposits", { ...value.deposits, autoTopUp: next })}
          disabled={disabled}
        />

        <RangeField
          label={t("history.withdrawalShare")}
          value={value.withdrawals.percentOfBalance}
          onChange={(next) => set("withdrawals", { ...value.withdrawals, percentOfBalance: next })}
          suffix="%"
          min={1}
          max={100}
          hint={t("history.withdrawalShareHint")}
          disabled={disabled}
          error={
            value.withdrawals.percentOfBalance.max < value.withdrawals.percentOfBalance.min
              ? t("history.rangeError")
              : null
          }
        />
        <MoneyField
          label={t("history.withdrawalFloor")}
          value={centsToInput(value.withdrawals.minCents)}
          onChange={(next) =>
            set("withdrawals", { ...value.withdrawals, minCents: parseMoney(next) ?? 0 })
          }
          hint={t("history.withdrawalFloorHint")}
          disabled={disabled}
        />
      </section>
    </div>
  );
}

/**
 * The order a day will read in, drawn as the chips the player's history shows.
 *
 * Operators asked for an order by writing it out — "Deposit > Bet1 > Win1 >
 * Bet2 > Win2 > Withdraw" — so the form writes it back the same way, for the
 * busiest day the profile allows. Long days are elided after the third slip.
 */
function OrderPreview({
  sequence,
  bets,
  cashFlowPercent,
}: {
  sequence: HistorySequence;
  bets: number;
  cashFlowPercent: number;
}) {
  const t = useT();

  const rows: { key: string; label: string | null; steps: Step[] }[] = [];
  if (cashFlowPercent > 0) {
    rows.push({
      key: "cash",
      label: cashFlowPercent < 100 ? t("history.order.cashDays", { percent: cashFlowPercent }) : null,
      steps: orderSteps(sequence, bets, true, t),
    });
  }
  if (cashFlowPercent < 100) {
    rows.push({
      key: "bets",
      label: t("history.order.betDays", { percent: 100 - cashFlowPercent }),
      steps: orderSteps(sequence, bets, false, t),
    });
  }

  const colour = { money: "var(--accent)", bet: "var(--text)", win: "var(--success)" };

  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={row.key} className="space-y-1">
          {row.label ? <p className="text-dim text-[11px]">{row.label}</p> : null}
          <div className="flex flex-wrap items-center gap-1 text-[11px]">
            {row.steps.map((step, index) => (
              <span key={step.key} className="flex items-center gap-1">
                {index > 0 ? <span className="text-dim">›</span> : null}
                <span
                  className="rounded-md px-1.5 py-0.5 font-semibold"
                  style={{ background: "var(--surface-inset)", color: colour[step.tone] }}
                >
                  {step.label}
                </span>
              </span>
            ))}
          </div>
        </div>
      ))}
      <p className="text-dim text-[11px]">{t("history.order.hint")}</p>
    </div>
  );
}

type Step = { key: string; label: string; tone: "money" | "bet" | "win" };

/** One day's records in order, with or without the deposit and withdrawal around them. */
function orderSteps(
  sequence: HistorySequence,
  bets: number,
  withCashFlow: boolean,
  t: ReturnType<typeof useT>,
): Step[] {
  const shown = Math.min(bets, 3);
  const slips = Array.from({ length: shown }, (_, i) => i + 1);
  const steps: Step[] = withCashFlow
    ? [{ key: "d", label: t("history.step.deposit"), tone: "money" }]
    : [];
  const lead = steps.length;
  if (sequence.settle === "each") {
    for (const n of slips) {
      steps.push({ key: `b${n}`, label: t("history.step.bet", { n }), tone: "bet" });
      steps.push({ key: `w${n}`, label: t("history.step.win", { n }), tone: "win" });
    }
  } else {
    for (const n of slips) steps.push({ key: `b${n}`, label: t("history.step.bet", { n }), tone: "bet" });
    for (const n of slips) steps.push({ key: `w${n}`, label: t("history.step.win", { n }), tone: "win" });
  }
  if (bets > shown) {
    const at = sequence.settle === "each" ? steps.length : lead + shown;
    steps.splice(at, 0, { key: "more", label: "…", tone: "bet" });
  }
  if (withCashFlow) steps.push({ key: "x", label: t("history.step.withdraw"), tone: "money" });
  return steps;
}

/**
 * Whether a profile is coherent enough to send.
 *
 * Only the rules the form itself can see. Everything that needs the whole run —
 * whether the deposits actually cover the stakes, say — is the API's to judge,
 * and it says so in `warnings` rather than refusing.
 */
export function profileIsValid(profile: HistoryProfile): boolean {
  const ranges = [
    profile.betsPerDay,
    profile.sequence.gapMinutes,
    profile.stakeCents,
    profile.intervalDays,
    profile.deposits.amountCents,
    profile.withdrawals.percentOfBalance,
  ];
  if (ranges.some((range) => range.max < range.min)) return false;
  if (!profile.from || !profile.to || profile.from > profile.to) return false;
  if (profile.to > today()) return false;
  if (windowMinutes(profile.window) === null || !profile.timeZone) return false;
  if (profile.pattern === "weekdays" && profile.daysOfWeek.length === 0) return false;
  if (profile.pattern === "interval" && profile.intervalDays.min < 1) return false;
  if (profile.cashFlowPercent < 0 || profile.cashFlowPercent > 100) return false;
  return profile.stakeCents.min > 0 && profile.deposits.amountCents.min > 0;
}
